import { BasicCard } from '@common/CardWithTitle/BasicCard';
import { BasicCardContent } from '@common/CardWithTitle/BasicCardContent';
import {
  KeyboardArrowDown,
  SyncAltOutlined,
  TrendingUp,
} from '@mui/icons-material';
import {
  Box,
  styled,
  Typography,
  useTheme,
  Menu,
  IconButton,
  Grid,
} from '@mui/material';
import { useAuditLogsStore } from '@stores/auditLogsStore';
import { useLocationsStore } from '@stores/locationsStore';
import { useProductsStore } from '@stores/productsStore';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from 'recharts';
import { LineChart, Line, YAxis } from 'recharts';
import { AuditLogsClient } from 'src/Clients/AuditLogsClient';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@Auth/useAuth';

interface MonthlyCount {
  month?: string;
  date?: string;
  count: number;
}

interface StockMovement {
  month?: string;
  date?: string;
  increase: number;
  decrease: number;
}

export const Dashboard = () => {
  const theme = useTheme();
  const [monthlyData, setMonthlyData] = useState<MonthlyCount[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [monthsToShow, setMonthsToShow] = useState<number>(3);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { getAuditLogsByOrganizationId, auditLogs } = useAuditLogsStore();
  const { baseUser } = useAuth();
  const { getLocationsByOrganizationId } = useLocationsStore();
  const { getProductsByOrganizationId } = useProductsStore();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (months: number) => {
    setMonthsToShow(months);
    handleClose();
  };

  useEffect(() => {
    if (baseUser?.organization_id) {
      getAuditLogsByOrganizationId(baseUser.organization_id);
      getLocationsByOrganizationId(baseUser.organization_id);
      getProductsByOrganizationId(baseUser.organization_id);
    }
  }, [
    baseUser?.organization_id,
    getAuditLogsByOrganizationId,
    getLocationsByOrganizationId,
    getProductsByOrganizationId,
  ]);

  useEffect(() => {
    const fetchMonthlyData = async () => {
      if (!baseUser?.organization_id) return;
      try {
        const [auditData, movementData] = await Promise.all([
          AuditLogsClient.getMonthlyAuditLogCountsByOrganizationId(
            baseUser.organization_id,
            monthsToShow
          ),
          AuditLogsClient.getMonthlyStockMovements(
            baseUser.organization_id,
            monthsToShow
          ),
        ]);
        setMonthlyData(auditData);
        setStockMovements(movementData);
      } catch (error) {
        console.error('Error fetching monthly data:', error);
      }
    };

    fetchMonthlyData();
  }, [baseUser?.organization_id, monthsToShow]);

  const formatDate = useCallback(
    (dateStr: string) => {
      if (!dateStr) return '';
      try {
        if (monthsToShow < 1) {
          // For daily view (when dateStr is YYYY-MM-DD)
          return format(parseISO(dateStr), 'EEE d');
        }
        // For monthly view (when dateStr is YYYY-MM)
        return format(parseISO(dateStr + '-01'), 'MMM yyyy');
      } catch (error) {
        console.error('Error formatting date:', error);
        return dateStr;
      }
    },
    [monthsToShow]
  );

  const chartData = useMemo(() => {
    return monthlyData.map((item) => ({
      name: formatDate(item.month || item.date || ''),
      value: item.count,
    }));
  }, [monthlyData, formatDate]);

  const stockChartData = useMemo(() => {
    return stockMovements.map((item) => ({
      name: formatDate(item.month || item.date || ''),
      increase: item.increase,
      decrease: item.decrease,
    }));
  }, [stockMovements, formatDate]);

  const getXAxisConfig = useMemo(() => {
    // Configure x-axis based on time range
    if (monthsToShow < 1) {
      // Daily view (7 days)
      return {
        angle: -45,
        textAnchor: 'end',
        interval: 0,
        height: 60,
      };
    } else if (monthsToShow <= 3) {
      // 3 months view
      return {
        angle: 0,
        textAnchor: 'middle',
        interval: 0,
        height: 30,
      };
    } else {
      // 6 months and 1 year view
      return {
        angle: -45,
        textAnchor: 'end',
        interval: monthsToShow > 6 ? 1 : 0, // Show every other label for 1 year
        height: 60,
      };
    }
  }, [monthsToShow]);

  return (
    <DashboardContainer>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: 2,
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          paddingBottom: '100px',
        }}
      >
        <Typography variant="h1">Dashboard</Typography>
        <Box>
          <SelectButton onClick={handleClick}>
            {monthsToShow < 1 ? `7 Days` : `${monthsToShow} Months`}{' '}
            <KeyboardArrowDown
              sx={{ fontSize: '14px', color: theme.palette.text.secondary }}
            />
          </SelectButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box
              sx={{
                flexDirection: 'column',
                width: '256px',
                padding: '4px',
              }}
            >
              <MenuItem onClick={() => handleMenuItemClick(7 / 30)}>
                7 Days
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick(3)}>
                3 Months
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick(6)}>
                6 Months
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick(12)}>
                1 Year
              </MenuItem>
            </Box>
          </Menu>
        </Box>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
            <BasicCard>
              <BasicCardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': { cursor: 'pointer' },
                        width: '100%',
                      }}
                      onClick={() => {}}
                    >
                      <IconButton
                        sx={{
                          background: theme.palette.action.hover,
                          borderRadius: '4px',
                          fontSize: '14px',
                        }}
                      >
                        <SyncAltOutlined fontSize="inherit" />
                      </IconButton>
                      <Typography variant="h4">Audit Logs</Typography>
                    </Box>
                    <Typography variant="subtitle1">Changes</Typography>
                    <Typography variant="body1">{auditLogs.length}</Typography>
                  </Box>
                </Box>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={chartData}>
                    <XAxis
                      dataKey="name"
                      axisLine={{ stroke: theme.palette.action.hover }}
                      tickLine={false}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 12,
                      }}
                      {...getXAxisConfig}
                    />
                    <Tooltip cursor={{ fill: theme.palette.action.hover }} />
                    <Bar dataKey="value" fill={theme.palette.primary.main} />
                  </BarChart>
                </ResponsiveContainer>
              </BasicCardContent>
            </BasicCard>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
            <BasicCard>
              <BasicCardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': { cursor: 'pointer' },
                        width: '100%',
                      }}
                      onClick={() => {}}
                    >
                      <IconButton
                        sx={{
                          background: theme.palette.action.hover,
                          borderRadius: '4px',
                          fontSize: '14px',
                        }}
                      >
                        <TrendingUp fontSize="inherit" />
                      </IconButton>
                      <Typography variant="h4">Stock Movement</Typography>
                    </Box>
                    <Typography variant="subtitle1">
                      Increases vs Decreases
                    </Typography>
                    <Typography variant="body1">
                      {stockMovements.reduce(
                        (acc, curr) => acc + curr.increase - curr.decrease,
                        0
                      )}
                    </Typography>
                  </Box>
                </Box>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={stockChartData}>
                    <XAxis
                      dataKey="name"
                      axisLine={{ stroke: theme.palette.action.hover }}
                      tickLine={false}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 12,
                      }}
                      {...getXAxisConfig}
                    />
                    <YAxis
                      axisLine={{ stroke: theme.palette.action.hover }}
                      tickLine={false}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 12,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="increase"
                      stroke={theme.palette.success.main}
                      strokeWidth={2}
                      dot={{ fill: theme.palette.success.main }}
                    />
                    <Line
                      type="monotone"
                      dataKey="decrease"
                      stroke={theme.palette.error.main}
                      strokeWidth={2}
                      dot={{ fill: theme.palette.error.main }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </BasicCardContent>
            </BasicCard>
          </Grid>
        </Grid>
      </Box>
    </DashboardContainer>
  );
};

const SelectButton = styled('button')(({ theme }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: '14px',
  height: '26px',
  gap: '4px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MenuItem = styled('li')(({ theme }) => ({
  padding: '6px 8px',
  height: '28px',
  fontSize: '14px',
  fontWeight: 400,
  color: theme.palette.mode === 'light' ? '#525252' : '#d4d4d4',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '4px',
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
  },
}));

const DashboardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  width: '100%',
  height: '100vh', // Account for header/navigation
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '0px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.action.hover,
  },
}));
